/**
 * R164: Reputation panel — toggle with `R`. Vanilla WoW «Reputation» tab.
 * Показывает все 14 фракций с tier + progress bar + текущая repation
 * (от localStorage 'aiwari-rep-{factionId}', server-side persist в TODO).
 *
 * Vanilla layout: список факций по 3 колонкам (Alliance/Horde/Neutral),
 * каждая строка: иконка + название + tier-pill + bar.
 */

import { useEffect, useState } from 'react';
import { REP_FACTIONS, REP_TIER_THRESHOLDS, tierForRep, tierColour } from '../data/reputations.js';
import { REP_REWARDS, type RepReward } from '../data/reputation-rewards.js';
import { ITEMS } from '../data/equipment.js';

const REP_REWARDS_BY_FACTION: Record<string, RepReward[]> = {};
for (const r of REP_REWARDS) {
  (REP_REWARDS_BY_FACTION[r.factionId] ??= []).push(r);
}

const STORAGE_PREFIX = 'aiwari-rep-';

function loadRep(factionId: string): number {
  try {
    return parseInt(localStorage.getItem(STORAGE_PREFIX + factionId) ?? '0', 10);
  } catch { return 0; }
}

export function ReputationPanel() {
  const [open, setOpen] = useState(false);
  const [reps, setReps] = useState<Record<string, number>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'r' || e.key === 'R' || e.key === 'к' || e.key === 'К') {
        // R164: только если не открыт другой modal — иначе R конфликтует с
        // running-shift (run hotkey зарезервирован).
        if (!e.shiftKey) return;  // Shift+R открывает Reputation
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Load all reputations on open.
    const map: Record<string, number> = {};
    for (const f of REP_FACTIONS) {
      map[f.id] = loadRep(f.id);
    }
    setReps(map);
  }, [open]);

  if (!open) return null;

  const allianceFactions = REP_FACTIONS.filter((f) => f.side === 'alliance');
  const hordeFactions = REP_FACTIONS.filter((f) => f.side === 'horde');
  const neutralFactions = REP_FACTIONS.filter((f) => f.side === 'neutral');

  return (
    <div className="absolute inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center pointer-events-auto"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="rounded-xl p-5 max-w-[860px] w-[92vw] max-h-[88vh] overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg, #1A0F0A, #0E0807)',
          border: '2px solid #C9A96A',
          boxShadow: '0 0 32px rgba(251,191,36,0.4)',
          fontFamily: 'system-ui',
        }}>
        <div className="flex items-baseline justify-between mb-4 pb-2 border-b" style={{ borderColor: '#C9A96A40' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8DCC0]Muted">Классическая эпоха</div>
            <h2 className="text-2xl font-bold text-[#E8C77B]" style={{ fontFamily: 'Cinzel, serif' }}>
              ⚜ Репутация
            </h2>
          </div>
          <button onClick={() => setOpen(false)} className="text-[#E8DCC0]Muted hover:text-[#E8DCC0] text-xl">×</button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <RepColumn title="Серебряная Заря" colour="#3B82F6" factions={allianceFactions} reps={reps} />
          <RepColumn title="Бурное Племя"    colour="#DC2626" factions={hordeFactions}    reps={reps} />
          <RepColumn title="Нейтральные"     colour="#9CA3AF" factions={neutralFactions} reps={reps} />
        </div>

        <div className="mt-5 pt-3 border-t text-[10px] text-[#E8DCC0]Muted flex flex-wrap gap-x-4 gap-y-1" style={{ borderColor: '#C9A96A30' }}>
          <span>Тиры:</span>
          {REP_TIER_THRESHOLDS.map((t) => (
            <span key={t.tier} style={{ color: tierColour(t.tier as never) }}>● {t.ru}</span>
          ))}
        </div>
        <div className="text-[10px] text-[#E8DCC0]Muted mt-2 italic">
          Shift+R закрыть. Репутация растёт за квесты, убийства мобов и события. Серверное сохранение — в следующем релизе.
        </div>
      </div>
    </div>
  );
}

function RepColumn({ title, colour, factions, reps }: {
  title: string; colour: string;
  factions: typeof REP_FACTIONS; reps: Record<string, number>;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider mb-2 pb-1 border-b" style={{ color: colour, borderColor: colour + '40' }}>
        {title}
      </div>
      <div className="space-y-2">
        {factions.map((f) => {
          const rep = reps[f.id] ?? 0;
          const ti = tierForRep(rep);
          const tCol = tierColour(ti.tier);
          // R202: rewards expansion (collapsible).
          const rewards = REP_REWARDS_BY_FACTION[f.id] ?? [];
          return (
            <div key={f.id} className="rounded-md p-2" style={{ background: `${f.colour}10`, border: `1px solid ${f.colour}30` }}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[14px]">{f.icon}</span>
                <span className="text-[12px] font-bold flex-1 truncate" style={{ color: f.colour, fontFamily: 'Cinzel, serif' }}>
                  {f.name.ru}
                </span>
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: tCol + '30', color: tCol, border: `1px solid ${tCol}80` }}>
                  {ti.ru}
                </span>
              </div>
              {/* Progress bar */}
              <div className="relative h-1.5 rounded-sm overflow-hidden" style={{ background: 'rgba(0,0,0,0.6)' }}>
                <div className="h-full transition-[width]" style={{ width: `${ti.pctInTier * 100}%`, background: tCol }} />
              </div>
              <div className="text-[9px] text-[#E8DCC0]Muted text-right mt-0.5 font-mono">{rep.toLocaleString()}</div>
              {/* R202: rewards list */}
              {rewards.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {rewards.map((rw) => {
                    const item = ITEMS.find((i) => i.id === rw.itemId);
                    if (!item) return null;
                    const tierMin = REP_TIER_THRESHOLDS.find((t) => t.tier === rw.tierRequired)?.min ?? 999999;
                    const unlocked = rep >= tierMin;
                    const reqColour = tierColour(rw.tierRequired);
                    return (
                      <div key={rw.itemId} className="flex items-baseline gap-1.5 text-[9px]"
                        style={{ opacity: unlocked ? 1 : 0.55 }}>
                        <span style={{ color: reqColour }}>{unlocked ? '✓' : '🔒'}</span>
                        <span className="truncate flex-1" style={{ color: item.tint ?? '#fff' }}>{item.name.ru}</span>
                        <span className="font-mono text-[#E8DCC0]Muted">{rw.price.toLocaleString('ru-RU')}💎</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
